// Copyright 2024 Sorama B.V.

import { Point2 } from "libraries/DataTypes/Point2";
import { Unit } from "units/Unit";
import { PsychoAcousticMetricLayoutProperties } from "./SpiderChartFactory";

export class LayoutBuilder {
    private layout: PsychoAcousticMetricLayoutProperties;

    /**
     * Initializes a new instance of the `LayoutBuilder` class.
     * Constructs an empty `PsychoAcousticMetricLayoutProperties` object.
     */
    constructor() {
        this.layout = {} as PsychoAcousticMetricLayoutProperties;
    }

    /**
     * Sets the display name for the layout.
     * @param displayName - The display name of the psychoacoustic metric.
     * @returns The current instance of the `LayoutBuilder` for chaining.
     */
    public setDisplayName(displayName: string): LayoutBuilder {
        this.layout.displayName = displayName;
        return this;
    }

    /**
     * Sets the point representing the maximum value for the layout.
     * @param maxValuePoint - The `Point2` representing the maximum value's position on the chart.
     * @returns The current instance of the `LayoutBuilder` for chaining.
     */
    public setMaxValuePoint(maxValuePoint: Point2): LayoutBuilder {
        this.layout.maxValuePoint = maxValuePoint;
        return this;
    }

    /**
     * Sets the point where the text (e.g., labels) will be displayed on the chart.
     * @param textPoint - The `Point2` where the label text will be placed on the chart.
     * @returns The current instance of the `LayoutBuilder` for chaining.
     */
    public setTextPoint(textPoint: Point2): LayoutBuilder {
        this.layout.textPoint = textPoint;
        return this;
    }

    /**
     * Sets the unit of measurement for the layout (e.g., "dB", "Hz").
     * @param unit - The unit associated with the psychoacoustic metric.
     * @returns The current instance of the `LayoutBuilder` for chaining.
     */
    public setUnit(unit: Unit): LayoutBuilder {
        this.layout.unit = unit;
        return this;
    }

    /**
     * Builds and returns the configured `PsychoAcousticMetricLayoutProperties` object.
     * This method finalizes the layout configuration.
     * @returns A fully constructed `PsychoAcousticMetricLayoutProperties` object.
     */
    public build(): PsychoAcousticMetricLayoutProperties {
        return this.layout;
    }
}
